<template>
  <div class="audio-toolbar">
    <!-- Primary: Playback controls + time -->
    <div class="audio-toolbar__primary">
      <!-- Previous -->
      <button
        v-if="showPrevNext"
        class="audio-toolbar__icon-btn"
        :disabled="!canPrev"
        aria-label="Previous track"
        @click="emit('prev')"
        v-html="skipPreviousIcon"
      />

      <!-- Play/Pause: prominent accent circle -->
      <button
        class="audio-toolbar__play-btn"
        :class="{ 'audio-toolbar__play-btn--playing': isPlaying }"
        :disabled="isLoading"
        :aria-label="isPlaying ? 'Pause' : 'Play'"
        :aria-pressed="isPlaying"
        @click="emit('togglePlay')"
      >
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          size="22"
          width="2"
          color="white"
        />
        <span v-else class="audio-toolbar__play-icon" v-html="isPlaying ? pauseIcon : playIcon" />
      </button>

      <!-- Next -->
      <button
        v-if="showPrevNext"
        class="audio-toolbar__icon-btn"
        :disabled="!canNext"
        aria-label="Next track"
        @click="emit('next')"
        v-html="skipNextIcon"
      />

      <span class="audio-toolbar__time" role="status" aria-live="off">
        {{ formattedCurrentTime }} / {{ formattedDuration }}
      </span>
    </div>

    <!-- Secondary: Additional controls -->
    <div class="audio-toolbar__secondary">
      <!-- Playback speed -->
      <v-menu v-if="showPlaybackSpeed" location="top">
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            variant="text"
            size="small"
            class="audio-toolbar__speed-btn"
            aria-label="Playback speed"
          >
            {{ formattedSpeed }}
          </v-btn>
        </template>
        <v-list density="compact" class="audio-toolbar__speed-list">
          <v-list-item
            v-for="speed in availableSpeeds"
            :key="speed"
            :active="playbackSpeed === speed"
            @click="emit('speedChange', speed)"
          >
            <v-list-item-title>{{ speed }}x</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-divider v-if="showPlaybackSpeed" vertical class="audio-toolbar__divider" />

      <!-- Volume controls -->
      <div v-if="showVolume" class="audio-toolbar__volume">
        <button
          class="audio-toolbar__icon-btn"
          :aria-label="isMuted ? 'Unmute' : 'Mute'"
          :aria-pressed="isMuted"
          @click="emit('toggleMute')"
          v-html="currentVolumeIcon"
        />
        <v-slider
          v-if="!isMobile"
          :model-value="isMuted ? 0 : volume * 100"
          :min="0"
          :max="100"
          hide-details
          density="compact"
          class="audio-toolbar__volume-slider"
          aria-label="Volume"
          @update:model-value="handleVolumeChange"
        />
      </div>

      <v-divider v-if="showVolume" vertical class="audio-toolbar__divider" />

      <!-- Loop toggle -->
      <!-- TODO: re-enable when loop feature is needed
      <v-btn
        v-if="showLoop"
        :icon="mdiRepeat"
        variant="text"
        size="small"
        :color="isLoop ? 'primary' : undefined"
        aria-label="Loop"
        :aria-pressed="isLoop"
        @click="emit('toggleLoop')"
      />
      -->

      <!-- Shuffle toggle -->
      <v-btn
        v-if="showShuffle"
        :icon="mdiShuffle"
        variant="text"
        size="small"
        :color="isShuffle ? 'primary' : undefined"
        aria-label="Shuffle"
        :aria-pressed="isShuffle"
        @click="emit('toggleShuffle')"
      />

      <!-- Auto-play next toggle -->
      <v-btn
        v-if="showAutoPlayNext"
        :icon="mdiPlaylistPlay"
        variant="text"
        size="small"
        :color="isAutoPlayNext ? 'primary' : undefined"
        :aria-label="t('player.controls.autoPlayNext')"
        :aria-pressed="isAutoPlayNext"
        @click="emit('toggleAutoPlayNext')"
      />

      <!-- Subtitles (CC) -->
      <template v-if="subtitleTracks.length > 0">
        <v-divider vertical class="audio-toolbar__divider" />
        <div
          v-if="subtitleTracks.length > 1"
          class="audio-toolbar__subtitles"
          ref="subtitlesContainerRef"
          @mouseenter="onSubtitlesMouseEnter"
          @mouseleave="onSubtitlesMouseLeave"
        >
          <button
            class="audio-toolbar__icon-btn"
            :class="{ 'audio-toolbar__icon-btn--active': subtitlesEnabled }"
            :aria-label="t('player.controls.subtitles')"
            :aria-pressed="subtitlesEnabled"
            :aria-expanded="subtitlesMenuOpen"
            aria-haspopup="menu"
            ref="subtitlesButtonRef"
            @click="onSubtitlesButtonClick"
            @keydown.arrow-up.prevent="openSubtitlesMenuAndFocus"
            @keydown.arrow-down.prevent="openSubtitlesMenuAndFocus"
            v-html="subtitlesEnabled ? closedCaptionsIcon : closedCaptionsOffIcon"
          />

          <transition name="audio-subtitles-menu">
            <div
              v-if="subtitlesMenuOpen"
              class="audio-toolbar__subtitles-popup"
              role="menu"
              :aria-label="t('player.controls.subtitles')"
              ref="subtitlesPopupRef"
              @keydown="onSubtitlesMenuKeydown"
            >
              <button
                v-for="(track, index) in subtitleTracks"
                :key="track.uid"
                class="audio-toolbar__subtitles-item"
                :class="{ 'audio-toolbar__subtitles-item--active': subtitlesEnabled && currentSubtitleTrackIndex === index }"
                role="menuitemradio"
                :aria-checked="subtitlesEnabled && currentSubtitleTrackIndex === index"
                @click="selectSubtitleTrack(index)"
              >
                <span class="audio-toolbar__subtitles-check" aria-hidden="true" v-html="checkIcon" />
                <span class="audio-toolbar__subtitles-label">{{ track.label }}</span>
              </button>

              <div class="audio-toolbar__subtitles-separator" role="separator" />

              <button
                class="audio-toolbar__subtitles-item"
                :class="{ 'audio-toolbar__subtitles-item--active': !subtitlesEnabled }"
                role="menuitemradio"
                :aria-checked="!subtitlesEnabled"
                @click="selectSubtitlesOff"
              >
                <span class="audio-toolbar__subtitles-check" aria-hidden="true" v-html="checkIcon" />
                <span class="audio-toolbar__subtitles-label">{{ t('player.controls.subtitlesOff') }}</span>
              </button>
            </div>
          </transition>
        </div>
        <button
          v-else
          class="audio-toolbar__icon-btn"
          :class="{ 'audio-toolbar__icon-btn--active': subtitlesEnabled }"
          :aria-label="t('player.controls.subtitles')"
          :aria-pressed="subtitlesEnabled"
          @click="emit('toggleSubtitles')"
          v-html="subtitlesEnabled ? closedCaptionsIcon : closedCaptionsOffIcon"
        />
      </template>

      <!-- Fullscreen -->
      <button
        v-if="showFullscreen"
        class="audio-toolbar__icon-btn"
        :aria-label="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
        @click="emit('toggleFullscreen')"
        v-html="isFullscreen ? fullscreenExitIcon : fullscreenIcon"
      />

    </div><!-- /secondary -->
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onBeforeUnmount } from 'vue'
import { useDisplay } from 'vuetify'
// mdiRepeat: unused while loop button is disabled
import { mdiShuffle, mdiPlaylistPlay } from '@mdi/js'
import {
  playIcon,
  pauseIcon,
  skipPreviousIcon,
  skipNextIcon,
  volumeHighIcon,
  volumeLowIcon,
  volumeMuteIcon,
  fullscreenIcon,
  fullscreenExitIcon,
  closedCaptionsIcon,
  closedCaptionsOffIcon,
  checkIcon,
} from '../../video/assets/icons'
import { formatTime, formatPlaybackSpeed, PLAYBACK_SPEEDS, type PlaybackSpeed } from '../utils/audio-utils'
import type { SubtitleTrack } from '../../types/asset'
import { useAudioI18n } from '../i18n'

const props = withDefaults(
  defineProps<{
    isPlaying: boolean
    isLoading?: boolean
    currentTime: number
    duration: number
    volume: number
    isMuted: boolean
    playbackSpeed: number
    isLoop: boolean
    isShuffle: boolean
    isAutoPlayNext: boolean
    isFullscreen: boolean
    canPrev: boolean
    canNext: boolean
    showPrevNext?: boolean
    showVolume?: boolean
    showPlaybackSpeed?: boolean
    showLoop?: boolean
    showShuffle?: boolean
    showAutoPlayNext?: boolean
    showFullscreen?: boolean
    subtitleTracks?: SubtitleTrack[]
    subtitlesEnabled?: boolean
    currentSubtitleTrackIndex?: number
  }>(),
  {
    isLoading: false,
    showPrevNext: true,
    showVolume: true,
    showPlaybackSpeed: true,
    showLoop: true,
    showShuffle: true,
    showAutoPlayNext: true,
    showFullscreen: true,
    subtitleTracks: () => [],
    subtitlesEnabled: false,
    currentSubtitleTrackIndex: 0,
  }
)

const emit = defineEmits<{
  togglePlay: []
  prev: []
  next: []
  toggleMute: []
  volumeChange: [volume: number]
  speedChange: [speed: number]
  toggleLoop: []
  toggleShuffle: []
  toggleAutoPlayNext: []
  toggleFullscreen: []
  toggleSubtitles: []
  subtitleTrackChange: [index: number]
}>()

// i18n
const { t } = useAudioI18n()

// Display helper for mobile detection
const { mobile: isMobile } = useDisplay()

// Available playback speeds
const availableSpeeds: readonly PlaybackSpeed[] = PLAYBACK_SPEEDS

// Computed
const formattedCurrentTime = computed(() => formatTime(props.currentTime))
const formattedDuration = computed(() => formatTime(props.duration))
const formattedSpeed = computed(() => formatPlaybackSpeed(props.playbackSpeed))

const currentVolumeIcon = computed(() => {
  if (props.isMuted || props.volume === 0) return volumeMuteIcon
  if (props.volume < 0.5) return volumeLowIcon
  return volumeHighIcon
})

// Event handlers
function handleVolumeChange(value: number): void {
  emit('volumeChange', value / 100)
}

// ── Subtitles menu (hover-driven popup) ──

const subtitlesContainerRef = ref<HTMLElement | null>(null)
const subtitlesButtonRef = ref<HTMLButtonElement | null>(null)
const subtitlesPopupRef = ref<HTMLElement | null>(null)
const subtitlesMenuOpen = ref(false)

let subtitlesOpenTimer: ReturnType<typeof setTimeout> | null = null
let subtitlesCloseTimer: ReturnType<typeof setTimeout> | null = null

const isTouchDevice = computed(() =>
  typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
)

function clearSubtitlesTimers(): void {
  if (subtitlesOpenTimer) { clearTimeout(subtitlesOpenTimer); subtitlesOpenTimer = null }
  if (subtitlesCloseTimer) { clearTimeout(subtitlesCloseTimer); subtitlesCloseTimer = null }
}

function onSubtitlesMouseEnter(): void {
  if (isTouchDevice.value) return
  clearSubtitlesTimers()
  subtitlesOpenTimer = setTimeout(() => {
    subtitlesMenuOpen.value = true
  }, 150)
}

function onSubtitlesMouseLeave(): void {
  if (isTouchDevice.value) return
  clearSubtitlesTimers()
  subtitlesCloseTimer = setTimeout(() => {
    subtitlesMenuOpen.value = false
  }, 250)
}

function onSubtitlesButtonClick(): void {
  if (isTouchDevice.value) {
    subtitlesMenuOpen.value = !subtitlesMenuOpen.value
  } else {
    emit('toggleSubtitles')
    subtitlesMenuOpen.value = false
  }
}

function closeSubtitlesMenu(): void {
  subtitlesMenuOpen.value = false
}

function selectSubtitleTrack(index: number): void {
  emit('subtitleTrackChange', index)
  closeSubtitlesMenu()
}

function selectSubtitlesOff(): void {
  if (props.subtitlesEnabled) emit('toggleSubtitles')
  closeSubtitlesMenu()
}

function openSubtitlesMenuAndFocus(): void {
  subtitlesMenuOpen.value = true
  nextTick(() => {
    const items = getSubtitlesItems()
    const activeIndex = items.findIndex(el => el.classList.contains('audio-toolbar__subtitles-item--active'))
    const target = activeIndex >= 0 ? items[activeIndex] : items[0]
    target?.focus()
  })
}

function onSubtitlesMenuKeydown(e: KeyboardEvent): void {
  const items = getSubtitlesItems()
  const currentIndex = items.indexOf(document.activeElement as HTMLElement)

  switch (e.key) {
    case 'ArrowDown': {
      e.preventDefault()
      const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0
      items[next]?.focus()
      break
    }
    case 'ArrowUp': {
      e.preventDefault()
      const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1
      items[prev]?.focus()
      break
    }
    case 'Escape':
      e.preventDefault()
      closeSubtitlesMenu()
      subtitlesButtonRef.value?.focus()
      break
    case 'Tab':
      closeSubtitlesMenu()
      break
  }
}

function getSubtitlesItems(): HTMLElement[] {
  if (!subtitlesPopupRef.value) return []
  return Array.from(subtitlesPopupRef.value.querySelectorAll<HTMLElement>('[role="menuitemradio"]'))
}

function onSubtitlesDocumentClick(e: MouseEvent): void {
  if (subtitlesContainerRef.value && !subtitlesContainerRef.value.contains(e.target as Node)) {
    closeSubtitlesMenu()
  }
}

watch(subtitlesMenuOpen, (open) => {
  if (open) {
    document.addEventListener('click', onSubtitlesDocumentClick, true)
  } else {
    document.removeEventListener('click', onSubtitlesDocumentClick, true)
  }
})

onBeforeUnmount(() => {
  clearSubtitlesTimers()
  document.removeEventListener('click', onSubtitlesDocumentClick, true)
})
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.audio-toolbar {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: $space-sm $space-lg;
  gap: $space-xs $space-sm;
  min-height: 56px;
  z-index: 1;

  // Primary group: prev/play/next + time — stays together
  &__primary {
    display: flex;
    align-items: center;
    gap: $space-sm;
    flex-shrink: 0;
  }

  // Secondary group: speed, volume, loop, shuffle, cc, fullscreen
  // On wide screens: aligns to the right. On narrow screens (via media query):
  // takes full width and centers, creating a second row.
  &__secondary {
    display: flex;
    align-items: center;
    gap: $space-xs;
    flex-wrap: wrap;
    flex: 1 1 auto;
    min-width: 0;
    justify-content: flex-end;
  }

  // Icon buttons (skip, volume, fullscreen, etc.)
  &__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--audio-icon-color, rgba(var(--v-theme-on-surface), $opacity-secondary));
    cursor: pointer;
    flex-shrink: 0;
    @include transition-fast;

    :deep(svg) {
      width: 18px;
      height: 18px;
    }

    &:hover:not(:disabled) {
      background: rgba(var(--v-theme-on-surface), 0.08);
      color: rgba(var(--v-theme-on-surface), $opacity-primary);
      transform: scale(1.1);
    }

    &:active:not(:disabled) {
      transform: scale(0.92);
    }

    &:disabled {
      opacity: $opacity-disabled;
      cursor: default;
    }

    @include focus-visible-only;

    @include reduced-motion {
      &:hover,
      &:active {
        transform: none;
      }
    }
  }

  &__play-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(svg) {
      width: 24px;
      height: 24px;
    }
  }

  // Prominent play/pause button
  &__play-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    flex-shrink: 0;
    background: var(--audio-accent-color, rgb(var(--v-theme-primary)));
    color: white;
    box-shadow: 0 2px 12px rgba(var(--audio-accent-rgb, var(--v-theme-primary)), 0.35);
    @include transition-fast;

    &:hover:not(:disabled) {
      transform: scale(1.08);
      box-shadow: 0 4px 18px rgba(var(--audio-accent-rgb, var(--v-theme-primary)), 0.45);
    }

    &:active:not(:disabled) {
      transform: scale(0.95);
    }

    &:disabled {
      opacity: $opacity-disabled;
      cursor: default;
    }

    @include focus-visible-only;

    @include reduced-motion {
      &:hover,
      &:active {
        transform: none;
      }
    }
  }

  &__time {
    font-size: $text-sm;
    font-weight: $font-medium;
    font-variant-numeric: tabular-nums;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    white-space: nowrap;
    letter-spacing: $tracking-wide;
  }

  &__divider {
    margin: 0 $space-xs;
    align-self: center;
    height: 16px;
  }

  &__volume {
    display: flex;
    align-items: center;
    gap: $space-xs;
  }

  &__volume-slider {
    width: 80px;
    min-width: 60px;

    :deep(.v-slider-track__background) {
      height: 3px;
      border-radius: 2px;
      opacity: 0.2;
    }

    :deep(.v-slider-track__fill) {
      height: 3px;
      border-radius: 2px;
    }

    :deep(.v-slider-thumb) {
      width: 12px;
      height: 12px;

      &::before {
        display: none;
      }
    }

    :deep(.v-slider-thumb__surface) {
      width: 12px;
      height: 12px;
      box-shadow: $shadow-sm;
    }
  }

  &__speed-btn {
    min-width: 44px;
    font-size: $text-xs;
    font-weight: $font-bold;
    letter-spacing: $tracking-wide;
    border-radius: $radius-full;
    background: rgba(var(--v-theme-on-surface), 0.06);
    @include transition-fast(background-color);

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.12);
    }
  }

  &__speed-list {
    min-width: 80px;
  }

  &__icon-btn--active {
    color: var(--audio-accent-color, rgb(var(--v-theme-primary)));
  }

  // Subtitles menu — custom hover-driven popup
  &__subtitles {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__subtitles-popup {
    position: absolute;
    bottom: calc(100% + #{$space-sm});
    right: 0;
    min-width: 200px;
    max-width: 280px;
    padding: $space-xs;
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-theme-on-surface), $opacity-border);
    border-radius: $radius-lg;
    box-shadow: $shadow-lg;
    backdrop-filter: blur(12px);
    z-index: 10;
  }

  &__subtitles-separator {
    height: 1px;
    margin: $space-xs $space-sm;
    background: rgba(var(--v-theme-on-surface), $opacity-border);
  }

  &__subtitles-item {
    display: flex;
    align-items: center;
    gap: $space-sm;
    width: 100%;
    padding: $space-sm $space-md;
    background: transparent;
    border: none;
    border-radius: $radius-md;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    font-size: $text-base;
    font-weight: $font-medium;
    font-family: inherit;
    cursor: pointer;
    text-align: left;
    white-space: nowrap;
    @include transition-fast(background-color);

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.06);
      color: rgba(var(--v-theme-on-surface), $opacity-primary);
    }

    &--active {
      color: var(--audio-accent-color, rgb(var(--v-theme-primary)));
    }

    &:focus-visible {
      outline: 2px solid var(--audio-accent-color, rgb(var(--v-theme-primary)));
      outline-offset: -2px;
    }
  }

  &__subtitles-check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    visibility: hidden;
    color: var(--audio-accent-color, rgb(var(--v-theme-primary)));

    :deep(svg) {
      width: 16px;
      height: 16px;
    }

    .audio-toolbar__subtitles-item--active & {
      visibility: visible;
    }
  }

  &__subtitles-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Vuetify buttons (loop, shuffle) — keep micro-interactions
  :deep(.v-btn--icon) {
    @include transition-fast(transform);

    &:hover {
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.92);
    }

    @include reduced-motion {
      &:hover,
      &:active {
        transform: none;
      }
    }
  }

  :deep(.v-btn) {
    @include focus-visible-only;
  }
}

// Subtitles popup enter/leave transition
.audio-subtitles-menu-enter-active,
.audio-subtitles-menu-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.audio-subtitles-menu-enter-from,
.audio-subtitles-menu-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.98);
}

@include reduced-motion {
  .audio-subtitles-menu-enter-active,
  .audio-subtitles-menu-leave-active {
    transition: none;
  }

  .audio-subtitles-menu-enter-from,
  .audio-subtitles-menu-leave-to {
    transform: none;
  }
}

// When secondary wraps to its own line, center it
@media (max-width: 600px) {
  .audio-toolbar {
    &__secondary {
      flex-basis: 100%;
      justify-content: center;
    }

    &__volume-slider {
      display: none;
    }
  }
}

// Small screens: compact buttons
@media (max-width: 480px) {
  .audio-toolbar {
    padding: $space-xs $space-sm;

    &__play-btn {
      width: 44px;
      height: 44px;
    }

    &__icon-btn {
      width: 32px;
      height: 32px;

      :deep(svg) {
        width: 16px;
        height: 16px;
      }
    }

    &__divider {
      display: none;
    }

    :deep(.v-btn--icon.v-btn--size-small) {
      width: 32px;
      height: 32px;
    }
  }
}

// Very narrow: hide volume entirely
@media (max-width: 360px) {
  .audio-toolbar {
    &__volume {
      display: none;
    }
  }
}
</style>
